import { useState, useEffect, useCallback } from 'react'
import { useAxiosApi } from './useAxiosApi'

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
}

export default function useWebPush() {
    const { requestGet, requestPost } = useAxiosApi()
    const [isSubscribed, setIsSubscribed] = useState(false)
    const [isSupported, setIsSupported] = useState(false)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const supported = 'serviceWorker' in navigator && 'PushManager' in window
        setIsSupported(supported)

        if (!supported) {
            setLoading(false)
            return
        }

        navigator.serviceWorker.register('/sw.js').then(async (registration) => {
            const subscription = await registration.pushManager.getSubscription()
            setIsSubscribed(!!subscription)
            setLoading(false)
        }).catch(() => {
            setLoading(false)
        })
    }, [])

    const subscribe = useCallback(async () => {
        setLoading(true)
        try {
            const response = await requestGet('/me/vapid-public-key')
            const vapidKey = response.data.key

            const registration = await navigator.serviceWorker.ready
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey),
            })

            const json = subscription.toJSON()
            await requestPost('/me/push-subscription', {
                endpoint: json.endpoint,
                keys: json.keys,
            })

            setIsSubscribed(true)
        } catch (err) {
            console.error('Push subscription failed:', err)
        } finally {
            setLoading(false)
        }
    }, [requestGet, requestPost])

    const unsubscribe = useCallback(async () => {
        setLoading(true)
        try {
            const registration = await navigator.serviceWorker.ready
            const subscription = await registration.pushManager.getSubscription()

            if (subscription) {
                await requestPost('/me/push-subscription/unsubscribe', {
                    endpoint: subscription.endpoint,
                })
                await subscription.unsubscribe()
            }

            setIsSubscribed(false)
        } catch (err) {
            console.error('Push unsubscribe failed:', err)
        } finally {
            setLoading(false)
        }
    }, [requestPost])

    const toggle = useCallback(() => {
        return isSubscribed ? unsubscribe() : subscribe()
    }, [isSubscribed, subscribe, unsubscribe])

    return { isSubscribed, isSupported, loading, toggle }
}
